// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  brokerageApiV2,
  BrokerageTagTypes,
} from '@shared/data-access/api-base/citec-brokerage-api';
import { rawApi } from './raw-api';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { currencyApi } from './currency-api';
import { getExchangeRates } from '@shared/data-access/transformers/currency/get-exchange-rates';
import { convertInstitutionsCurrency } from '@shared/data-access/transformers/advisory/convert-institutions-currency';
import type * as BrokerageTypes from '@shared/data-access/types/brokerage-types';
import { pollWithBackoff } from '@shared/data-access/utils/poll-with-backoff';
import { isUndefined } from '@shared/utils';
import { SharedTagTypes } from '@shared/data-access/utils/constants';
import { convertAccountsPositionsCurrency } from '@shared/data-access/transformers/advisory/convert-accounts-positions-currency';
import { buildAccountsPositionsData } from '@shared/data-access/transformers/advisory/build-accounts-positions-data';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import {
  type AccountsTransactionsData,
  type AccountsPositionsData,
} from '@shared/data-access/interfaces/api/brokerage/brokerage';
import {
  clientsSlice,
  selectClientInstitutions,
  selectClientInstitutionsNames,
  selectClientProductsAccountsByInstitutionId,
} from '@shared/data-access/slices/clients';
import { convertAccountsTransactionsCurrency } from '@shared/data-access/transformers/advisory/convert-accounts-transactions-currency';
import { buildAccountsTransactionsData } from '@shared/data-access/transformers/advisory/build-accounts-transactions-data';
import {
  advisoryRepositorySlice,
  getCachedPositionsDiff,
  getCachedBalancesHistoricalDiff,
  getCachedTransactionsDiff,
  getCachedAccountsDebtDiff,
  transformPositionsForCache,
  transformTransactionsForCache,
  transformBalancesHistoricalForCache,
  transformAccountsDebtForCache,
} from '@shared/data-access/slices/advisory-repository';

/**
 * Advisory API v2 – Processed endpoints for advisory/aggregation flows.
 *
 * Builds on {@link rawApi} raw endpoints with:
 *
 * - **Cache:** Advisory repository cache for positions, historical balances and
 *   transactions (per institution, currency, client and date range). Returns cached
 *   data when complete; otherwise requests only missing IBANs and merges with
 *   cache.
 * - **Conversion:** Currency conversion to the user's account currency.
 * - **Enrichment:** Institution names, asset details (e.g. from assets API for
 *   positions).
 *
 * Callers pass `institution_id` and `currency`; IBANs are resolved from active
 * client state (e.g. via `selectClientProductsAccountsByInstitutionId`).
 */
export const advisoryApi = brokerageApiV2.injectEndpoints({
  endpoints: build => ({
    /**
     * Get all clients with their institutions and products.
     *
     * Retrieves a list of all clients associated with the authenticated user,
     * including their institutions and financial products. All currency values are
     * automatically converted to the user's account currency.
     *
     * @remarks
     * This endpoint returns comprehensive client information including email, name,
     * status, and associated institutions with their products (accounts, cards,
     * loans, portfolios). All monetary values are converted to the user's selected
     * account currency using current exchange rates.
     *
     * **Base endpoint:** `GET /v2/advisory/clients/`
     * @example
     *
     * ```tsx
     * const { data: clients, isLoading } =
     *   advisoryApi.useGetAdvisoryClientsQuery();
     *
     * // Display clients with converted currency values
     * clients?.forEach(client => {
     *   console.log(`Client: ${client.name} (${client.email})`);
     *   client.institutions.forEach(institution => {
     *     // All currency values are already converted to user's account currency
     *     institution.products.accounts.forEach(account => {
     *       console.log(`Cash: ${account.cash} (converted)`);
     *     });
     *   });
     * });
     * ```
     *
     * @returns An array of {@link Client} containing client information including
     *   email, name, ID, status, and associated institutions with currency values
     *   converted to the user's account currency.
     */
    getAdvisoryClients: build.query<
      BrokerageTypes.GetAdvisoryClientsResponseBody,
      BrokerageTypes.GetAdvisoryClientsParams
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryClientsRaw.initiate(params, {
            forceRefetch: api.forced,
          }),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data! };
      },
      providesTags: result =>
        result
          ? [
              { type: BrokerageTagTypes.Client, id: 'LIST' },
              ...result.data.map(client => ({
                type: BrokerageTagTypes.Client as const,
                id: client.id,
              })),
            ]
          : [{ type: BrokerageTagTypes.Client, id: 'LIST' }],
    }),

    /**
     * Get all available linked institutions with their accounts.
     *
     * Retrieves a list of all financial institutions (banks, brokerages, etc.) that
     * are linked to the authenticated user's account, including their account
     * details, sync status, and available products. All currency values are
     * automatically converted to the user's account currency.
     *
     * @remarks
     * This endpoint returns comprehensive institution information including
     * provider type (Plaid, Tink, Fintech, Wealth Reader, CITEC, PSN), last sync
     * timestamp, available products (accounts, cards, loans, portfolios), and any
     * error information. All monetary values are converted to the user's selected
     * account currency using current exchange rates.
     *
     * **Base endpoint:** `GET /v2/advisory/institutions/`
     * @example
     *
     * ```tsx
     * const { data: institutions, isLoading } =
     *   advisoryApi.useGetAdvisoryInstitutionsQuery({
     *     currency: AccountCurrency.EUR,
     *     client_id: 123,
     *   });
     *
     * // Display institutions with converted currency values
     * institutions?.forEach(institution => {
     *   console.log(`Institution: ${institution.name}`);
     *   console.log(`Provider: ${institution.provider}`);
     *   console.log(`Last sync: ${institution.last_sync || 'Never'}`);
     *   if (institution.error) {
     *     console.log(`Error: ${institution.error}`);
     *   }
     *   // All currency values are already converted to user's account currency
     *   institution.products.accounts.forEach(account => {
     *     console.log(`Cash: ${account.cash} (converted)`);
     *   });
     * });
     * ```
     *
     * @param params.currency - Account currency for conversion
     * @param params.client_id - Optional client ID (advisor context)
     * @returns An array of {@link BrokerageTypes.AdvisoryInstitution} containing
     *   institution information including ID, name, provider, logo URL, last sync
     *   timestamp, available products with currency values converted to the user's
     *   account currency, and error information.
     */
    getAdvisoryInstitutions: build.query<
      BrokerageTypes.AdvisoryInstitution[],
      { currency: AccountCurrency } & BrokerageTypes.GetAdvisoryInstitutionsParams
    >({
      queryFn: async ({ currency, client_id }, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryInstitutionsRaw.initiate(
            { client_id },
            {
              forceRefetch: api.forced,
            },
          ),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const institutions = response.data ?? [];

        const currencies = new Set(
          institutions.flatMap(({ products }) =>
            [
              ...products.accounts.map(account => account.currency),
              ...products.cards.map(card => card.currency),
              ...products.loans.map(loan => loan.currency),
              ...products.portfolios.map(portfolio => portfolio.currency),
            ].filter((currency): currency is string => currency !== null),
          ),
        );

        const getPrices = (currencies: string[]) =>
          api.dispatch(currencyApi.endpoints.currencyPrices.initiate(currencies));

        const exchangeRates = await getExchangeRates(
          Array.from(currencies),
          currency,
          getPrices,
        );

        const convertedInstitutions = convertInstitutionsCurrency(
          institutions,
          exchangeRates,
        );

        return { data: convertedInstitutions };
      },
      providesTags: result =>
        result
          ? [
              { type: BrokerageTagTypes.Institution, id: 'LIST' },
              ...result.map(inst => ({
                type: BrokerageTagTypes.Institution as const,
                id: inst.id,
              })),
              SharedTagTypes.CurrencyConversion,
            ]
          : [
              { type: BrokerageTagTypes.Institution, id: 'LIST' },
              SharedTagTypes.CurrencyConversion,
            ],
    }),

    /**
     * Search available bank institutions to connect.
     *
     * Retrieves institutions from the providers catalog filtered by optional search
     * text and country code.
     *
     * @remarks
     * **Base endpoint:** `GET /v2/advisory/institutions/search/`
     * @example
     *
     * ```tsx
     * const { data: institutions } =
     *   advisoryApi.useSearchAdvisoryInstitutionsQuery({
     *     country: 'ES',
     *     q: 'santander',
     *   });
     * ```
     */
    searchAdvisoryInstitutions: build.query<
      BrokerageTypes.SearchAdvisoryInstitutionsResponseBody,
      BrokerageTypes.SearchAdvisoryInstitutionsParams
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.searchAdvisoryInstitutionsRaw.initiate(params, {
            forceRefetch: api.forced,
          }),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data ?? [] };
      },
      providesTags: result =>
        result
          ? [
              { type: BrokerageTagTypes.Institution, id: 'SEARCH' },
              ...result.map(institution => ({
                type: BrokerageTagTypes.Institution as const,
                id: institution.id,
              })),
            ]
          : [{ type: BrokerageTagTypes.Institution, id: 'SEARCH' }],
    }),

    /**
     * Get positions for advisory accounts of an institution.
     *
     * Retrieves investment positions for all accounts of the given institution
     * (IBANs are resolved from the active client state). Data is cached per
     * institution, currency and client; only missing IBANs are requested from the
     * API. All currency values are converted to the user's account currency.
     *
     * @remarks
     * **Cache:** Returns cached data when all accounts for the institution are
     * already in cache. Otherwise requests only missing IBANs (or all when
     * `forceRefetch`). Cached and fetched results are merged.
     *
     * **Data:** Response includes ISIN, ticker, quantity, price, investment value,
     * asset type; enriched with asset class and details (logo, sector) from the
     * assets API. Holdings with the same ticker/ISIN are merged. All monetary
     * values are converted to the user's account currency.
     *
     * **Base endpoint:** `POST /v2/advisory/accounts/positions`
     * @example
     *
     * ```tsx
     * const { data: positions } =
     *   advisoryApi.useGetAdvisoryAccountsPositionsQuery({
     *     institution_id: 'inst_123',
     *     currency: 'EUR',
     *     client_id: 123,
     *   });
     *
     * positions?.forEach(account => {
     *   console.log(
     *     `Institution: ${account.institution_id}, IBAN: ${account.iban}`,
     *   );
     *   account.holdings.forEach(holding => {
     *     console.log(`${holding.name}: ${holding.investment} (converted)`);
     *   });
     * });
     * ```
     *
     * @param params.institution_id - Institution ID; accounts (IBANs) are derived
     *   from active client state
     * @param params.currency - Account currency for conversion
     * @param params.client_id - Optional client ID to filter positions
     * @returns An array of {@link AccountsPositionsData} with positions grouped by
     *   account
     */
    getAdvisoryAccountsPositions: build.query<
      AccountsPositionsData[],
      Omit<BrokerageTypes.GetAdvisoryAccountsPositionsRequestBody, 'ibans'> & {
        institution_id: string;
        currency: AccountCurrency;
      }
    >({
      queryFn: async ({ currency, institution_id, ...params }, api) => {
        const institutions = selectClientInstitutionsNames(api.getState() as any);

        const ibans = selectClientProductsAccountsByInstitutionId(
          api.getState() as any,
          institution_id,
        );

        const { cached, missingIbans } = getCachedPositionsDiff(
          api.getState() as any,
          currency,
          params.client_id ?? 0,
          ibans,
        );

        const payload =
          api.forced && missingIbans.length > 0 ? missingIbans : undefined;

        if (missingIbans.length === 0) {
          return {
            data: cached,
          };
        }

        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryAccountsPositionsRaw.initiate(
            {
              ...params,
              ibans: payload,
            },
            {
              forceRefetch: api.forced,
            },
          ),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const accountsPositions = response.data ?? [];

        const convertedAccountsPositions = convertAccountsPositionsCurrency(
          accountsPositions,
          currency,
        );

        const accountsPositionsData = await buildAccountsPositionsData(
          convertedAccountsPositions,
          institutions,
          api.dispatch,
        );

        api.dispatch(
          advisoryRepositorySlice.actions.upsertAdvisoryPositions(
            transformPositionsForCache(
              accountsPositionsData,
              currency,
              params.client_id ?? 0,
            ),
          ),
        );

        const data = cached.concat(accountsPositionsData);

        return {
          data,
        };
      },
      providesTags: (result, error, params) =>
        result
          ? [
              {
                type: BrokerageTagTypes.Client as const,
                id: `POSITIONS_${params.client_id || 'ALL'}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `INSTITUTION_${params.institution_id}`,
              },
              SharedTagTypes.CurrencyConversion,
            ]
          : [],
    }),

    /**
     * Get historical balances for advisory accounts of an institution.
     *
     * Retrieves historical balance data for all accounts of the given institution
     * (IBANs from active client state) within a date range: cash, debt, investment,
     * and net value over time. Data is cached per institution, currency, client and
     * date range; only missing IBANs are requested. All values are converted to the
     * user's account currency.
     *
     * @remarks
     * **Cache:** Returns cached data when all accounts for the institution are
     * already in cache for the date range. Otherwise requests only missing IBANs
     * (or all when `forceRefetch`). Cached and fetched results are merged.
     *
     * **Data:** Response includes institution_id, iban, last_sync and
     * historical_balances (dates, cash, debt, investment, net_value arrays).
     *
     * **Base endpoint:** `POST /v2/advisory/accounts/balances/historical/`
     * @example
     *
     * ```tsx
     * const { data: balances } =
     *   advisoryApi.useGetAdvisoryAccountsBalancesHistoricalQuery({
     *     institution_id: 'inst_123',
     *     currency: 'EUR',
     *     client_id: 123,
     *     start_date: '2024-01-01',
     *     end_date: '2024-12-31',
     *   });
     *
     * balances?.forEach(account => {
     *   console.log(`${account.institution_id} / ${account.iban}`);
     *   console.log(`Points: ${account.historical_balances.dates.length}`);
     * });
     * ```
     *
     * @param params.institution_id - Institution ID; accounts (IBANs) from active
     *   client state
     * @param params.currency - Account currency for conversion
     * @param params.client_id - Optional client ID
     * @param params.start_date - Start date for historical range (ISO 8601)
     * @param params.end_date - End date for historical range (ISO 8601)
     * @returns An array of {@link BrokerageTypes.AccountHistoricalBalances}
     */
    getAdvisoryAccountsBalancesHistorical: build.query<
      BrokerageTypes.GetAdvisoryAccountsBalancesHistoricalResponseBody,
      Omit<
        BrokerageTypes.GetAdvisoryAccountsBalancesHistoricalRequestBody,
        'currency'
      > & {
        institution_id: string;
        currency: AccountCurrency;
      }
    >({
      queryFn: async ({ institution_id, currency, ...params }, api) => {
        const ibans = selectClientProductsAccountsByInstitutionId(
          api.getState() as any,
          institution_id,
        );

        const { cached, missingIbans } = getCachedBalancesHistoricalDiff(
          api.getState() as any,
          currency,
          params.client_id ?? 0,
          ibans,
          params.start_date,
          params.end_date,
        );

        const payload =
          api.forced && missingIbans.length > 0 ? missingIbans : undefined;

        if (missingIbans.length === 0) {
          return {
            data: cached,
          };
        }

        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryAccountsBalancesHistoricalRaw.initiate(
            {
              ...params,
              currency,
              ibans: payload,
            },
            {
              forceRefetch: api.forced,
            },
          ),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const accountsBalancesHistorical = response.data ?? [];

        api.dispatch(
          advisoryRepositorySlice.actions.upsertAdvisoryBalancesHistorical(
            transformBalancesHistoricalForCache(
              accountsBalancesHistorical,
              currency,
              params.client_id ?? 0,
              params.start_date,
              params.end_date,
            ),
          ),
        );

        const data = cached.concat(accountsBalancesHistorical);

        return { data };
      },
      providesTags: (result, error, params) =>
        result
          ? [
              {
                type: BrokerageTagTypes.Client as const,
                id: `BALANCES_HISTORICAL_${params.client_id || 'ALL'}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `DATE_RANGE_${params.start_date}_${params.end_date}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `INSTITUTION_${params.institution_id}`,
              },
              SharedTagTypes.CurrencyConversion,
            ]
          : [],
    }),

    /**
     * Get transactions for advisory accounts of an institution.
     *
     * Retrieves transaction history for all accounts of the given institution
     * (IBANs from active client state) from a start date. Data is cached per
     * institution, currency and client; only missing IBANs are requested. All
     * currency values are converted to the user's account currency.
     *
     * @remarks
     * **Cache:** Returns cached data when all accounts for the institution are
     * already in cache for the start date. Otherwise requests only missing IBANs
     * (or all when `forceRefetch`). Cached and fetched results are merged.
     *
     * **Data:** Response includes transaction type, asset (ISIN, ticker, name),
     * quantity, price, amount, date; enriched with institution metadata.
     * local_price is the price converted to the user's account currency.
     *
     * **Base endpoint:** `POST /v2/advisory/accounts/transactions`
     * @example
     *
     * ```tsx
     * const { data: transactions } =
     *   advisoryApi.useGetAdvisoryAccountsTransactionsQuery({
     *     institution_id: 'inst_123',
     *     currency: 'EUR',
     *     client_id: 123,
     *     start_date: '2024-01-01',
     *   });
     *
     * transactions?.forEach(account => {
     *   console.log(`${account.institution_name} / ${account.iban}`);
     *   account.transactions.forEach(tx =>
     *     console.log(`${tx.date}: ${tx.type} - ${tx.local_price}`),
     *   );
     * });
     * ```
     *
     * @param params.institution_id - Institution ID; accounts (IBANs) from active
     *   client state
     * @param params.currency - Account currency for conversion
     * @param params.client_id - Optional client ID
     * @param params.start_date - Start date (ISO 8601); transactions from this date
     *   (inclusive)
     * @returns An array of {@link AccountsTransactionsData} grouped by account
     */
    getAdvisoryAccountsTransactions: build.query<
      AccountsTransactionsData[],
      BrokerageTypes.GetAdvisoryAccountsTransactionsRequestBody & {
        institution_id: string;
        currency: AccountCurrency;
      }
    >({
      queryFn: async ({ institution_id, currency, ...params }, api) => {
        const institutions = selectClientInstitutions(api.getState() as any);

        const ibans = selectClientProductsAccountsByInstitutionId(
          api.getState() as any,
          institution_id,
        );

        const { cached, missingIbans } = getCachedTransactionsDiff(
          api.getState() as any,
          currency,
          params.client_id ?? 0,
          ibans,
          params.start_date,
        );

        const payload =
          api.forced && missingIbans.length > 0 ? missingIbans : undefined;

        if (missingIbans.length === 0) {
          return {
            data: cached,
          };
        }

        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryAccountsTransactionsRaw.initiate(
            {
              ...params,
              ibans: payload,
            },
            {
              forceRefetch: api.forced,
            },
          ),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const accountsTransactions = response.data ?? [];

        const currencies = new Set(
          accountsTransactions
            .flatMap(account =>
              account.transactions.map(transaction => transaction.currency),
            )
            .filter((currency): currency is string => currency !== null),
        );

        const getPrices = (currencies: string[]) =>
          api.dispatch(currencyApi.endpoints.currencyPrices.initiate(currencies));

        const exchangeRates = await getExchangeRates(
          Array.from(currencies),
          currency,
          getPrices,
        );

        const accountsTransactionsData = buildAccountsTransactionsData(
          accountsTransactions,
          institutions,
        );

        const convertedAccountsTransactions = convertAccountsTransactionsCurrency(
          accountsTransactionsData,
          exchangeRates,
        );

        api.dispatch(
          advisoryRepositorySlice.actions.upsertAdvisoryTransactions(
            transformTransactionsForCache(
              convertedAccountsTransactions,
              currency,
              params.client_id ?? 0,
              params.start_date,
            ),
          ),
        );

        const data = cached.concat(convertedAccountsTransactions);

        return { data };
      },
      providesTags: (result, error, params) =>
        result
          ? [
              {
                type: BrokerageTagTypes.Client as const,
                id: `TRANSACTIONS_${params.client_id || 'ALL'}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `START_DATE_${params.start_date}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `INSTITUTION_${params.institution_id}`,
              },
              SharedTagTypes.CurrencyConversion,
            ]
          : [],
    }),

    /**
     * Get a link token for the Plaid Link flow.
     *
     * Generates a link token to initialize the Plaid Link component for connecting
     * a financial institution. The token is required to start the Plaid
     * authentication flow.
     *
     * @remarks
     * Exposed as a mutation because the token is generated at request time and must
     * not be cached. Use it when opening Plaid Link (e.g. on button click). An
     * optional `institution_id` pre-selects a specific institution.
     *
     * **Base endpoint:** `GET /v2/advisory/institutions/link/`
     * @example
     *
     * ```tsx
     * const [getLinkToken, { data, isLoading }] =
     *   advisoryApi.useGetAdvisoryInstitutionsLinkMutation();
     *
     * const handleOpenPlaid = async () => {
     *   const result = await getLinkToken({
     *     institution_id: 'ins_xxx',
     *   }).unwrap();
     *   plaidLink.open({ token: result.link_token });
     * };
     * ```
     *
     * @param params - See {@link BrokerageTypes.GetAdvisoryInstitutionsLinkParams}
     * @returns {@link BrokerageTypes.GetAdvisoryInstitutionsLinkResponseBody}
     *   (link token for Plaid Link).
     */
    getAdvisoryInstitutionsLink: build.mutation<
      BrokerageTypes.GetAdvisoryInstitutionsLinkResponseBody,
      BrokerageTypes.GetAdvisoryInstitutionsLinkParams
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryInstitutionsLinkRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      invalidatesTags: [],
    }),

    /**
     * Connect a financial institution via provider credentials.
     *
     * Establishes a connection by exchanging provider credentials (e.g. Plaid
     * public token) for an access token and creating the institution record. Used
     * after the user completes the Plaid Link (or similar) flow.
     *
     * @remarks
     * **Base endpoint:** `POST /v2/advisory/institutions/`
     * @example
     *
     * ```tsx
     * const [createInstitution, { isLoading }] =
     *   advisoryApi.useCreateAdvisoryInstitutionsMutation();
     *
     * await createInstitution({
     *   public_token: 'plaid_public_xxx',
     *   provider: 'plaid',
     * });
     * ```
     *
     * @param params - See
     *   {@link BrokerageTypes.CreateAdvisoryInstitutionsRequestBody}
     * @returns {@link BrokerageTypes.CreateAdvisoryInstitutionsResponseBody}
     */
    createAdvisoryInstitutions: build.mutation<
      BrokerageTypes.CreateAdvisoryInstitutionsResponseBody,
      BrokerageTypes.CreateAdvisoryInstitutionsRequestBody
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createAdvisoryInstitutionsRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data: institution } = await queryFulfilled;

          // Clear advisory repository cache for the institution in case of reconnection
          // Using clientId: 0 to clear cache for all clients with this institution
          if (!isUndefined(institution?.id)) {
            dispatch(
              advisoryRepositorySlice.actions.resetByClientAndInstitutionId({
                clientId: 0,
                institutions: [institution.id],
              }),
            );
          }

          if (!institution?.is_oneshot)
            dispatch(clientsSlice.actions.addPendingInstitution(institution));
        } catch (error) {
          console.error('Error in createAdvisoryInstitutions:', error);
        }
      },
      invalidatesTags: (result, error) =>
        result && !error
          ? [
              // { type: BrokerageTagTypes.Client, id: 'LIST' },
              // { type: BrokerageTagTypes.Institution, id: 'LIST' },
              // { type: BrokerageTagTypes.Aggregator, id: `INSTITUTION_${result.id}` },
            ]
          : [],
    }),

    /**
     * Bulk upload manual institution account data.
     *
     * Submits institution, accounts, positions, transactions, and debts from the
     * manual bank upload wizard. Row-level validation errors are returned in the
     * response body; callers decide how to surface them in the UI.
     *
     * @remarks
     * **Base endpoint:** `POST /v2/advisory/accounts/citec/`
     * @example
     *
     * ```tsx
     * const [createAccountsCitec, { isLoading }] =
     *   advisoryApi.useCreateAdvisoryAccountsCitecMutation();
     *
     * await createAccountsCitec({
     *   institution: { name: 'My Bank', logo_url: 'https://example.com/logo.png' },
     *   accounts: [{ iban: 'ES123', name: 'Checking', currency: 'EUR', ... }],
     * });
     * ```
     *
     * @param params - See
     *   {@link BrokerageTypes.CreateAdvisoryAccountsCitecRequestBody}
     * @returns {@link BrokerageTypes.CreateAdvisoryAccountsCitecResponseBody}
     */
    createAdvisoryAccountsCitec: build.mutation<
      BrokerageTypes.CreateAdvisoryAccountsCitecResponseBody,
      BrokerageTypes.CreateAdvisoryAccountsCitecRequestBody
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createAdvisoryAccountsCitecRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          dispatch(
            advisoryRepositorySlice.actions.resetByClientId({ clientId: 0 }),
          );
        } catch (error) {
          console.error('Error in createAdvisoryAccountsCitec:', error);
        }
      },
      invalidatesTags: (result, error) =>
        result && !error
          ? [
              { type: BrokerageTagTypes.Client, id: 'LIST' },
              { type: BrokerageTagTypes.Institution, id: 'LIST' },
              {
                type: BrokerageTagTypes.Aggregator,
                id: 'INSTITUTION_ALL',
              },
            ]
          : [],
    }),

    /**
     * Partially update manual institution account data.
     *
     * Upserts institution metadata and optional financial rows for an existing
     * manual Citec institution. Send only the sections that changed; include
     * `institution.id` to identify the target bank.
     *
     * @remarks
     * **Base endpoint:** `POST /v2/advisory/accounts/citec/`
     * @example
     *
     * ```tsx
     * const [updateAccountsCitec, { isLoading }] =
     *   advisoryApi.useUpdateAdvisoryAccountsCitecMutation();
     *
     * await updateAccountsCitec({
     *   institution: {
     *     id: 'inst_manual_1',
     *     name: 'Updated Bank',
     *     logo_url: 'https://example.com/logo.png',
     *   },
     * });
     * ```
     *
     * @param params - See
     *   {@link BrokerageTypes.UpdateAdvisoryAccountsCitecRequestBody}
     * @returns {@link BrokerageTypes.UpdateAdvisoryAccountsCitecResponseBody}
     */
    updateAdvisoryAccountsCitec: build.mutation<
      BrokerageTypes.UpdateAdvisoryAccountsCitecResponseBody,
      BrokerageTypes.UpdateAdvisoryAccountsCitecRequestBody
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.updateAdvisoryAccountsCitecRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          dispatch(clientsSlice.actions.patchManualInstitutionData(params));

          dispatch(
            advisoryRepositorySlice.actions.resetByClientAndInstitutionId({
              clientId: 0,
              institutions: [params.institution.id],
            }),
          );
        } catch (error) {
          console.error('Error in updateAdvisoryAccountsCitec:', error);
        }
      },
      invalidatesTags: (_result, _error, { institution }) => [
        { type: BrokerageTagTypes.Client, id: 'LIST' },
        { type: BrokerageTagTypes.Institution, id: 'LIST' },
        { type: BrokerageTagTypes.Institution, id: institution.id },
        {
          type: BrokerageTagTypes.Aggregator,
          id: `INSTITUTION_${institution.id}`,
        },
      ],
    }),

    /**
     * Remove a linked institution and its synced data.
     *
     * Permanently disconnects the institution and deletes all associated account
     * data, transactions, and balances. On success, updates the clients slice to
     * remove the institution from the active client.
     *
     * @remarks
     * **Base endpoint:** `DELETE /v2/advisory/institutions/{institution_id}/`
     * @example
     *
     * ```tsx
     * const [deleteInstitution, { isLoading, isSuccess }] =
     *   advisoryApi.useDeleteAdvisoryInstitutionsMutation();
     *
     * await deleteInstitution({ institution_id: 'ins_123' });
     * ```
     *
     * @param params - See
     *   {@link BrokerageTypes.DeleteAdvisoryInstitutionsInstitutionIdParams}
     * @returns Void on success.
     */
    deleteAdvisoryInstitutions: build.mutation<
      void,
      BrokerageTypes.DeleteAdvisoryInstitutionsInstitutionIdParams
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.deleteAdvisoryInstitutionsInstitutionIdRaw.initiate(
            params,
          ),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          dispatch(
            clientsSlice.actions.removeInstitution({
              client_id: 0,
              institutionId: params.institution_id,
            }),
          );

          dispatch(
            advisoryRepositorySlice.actions.resetByClientAndInstitutionId({
              clientId: 0,
              institutions: [params.institution_id],
            }),
          );
        } catch (error) {
          console.error('Error in removeInstitution:', error);
        }
      },
      invalidatesTags: (result, error) =>
        !isUndefined(result) && !error
          ? [
              { type: BrokerageTagTypes.Client, id: 'LIST' },
              { type: BrokerageTagTypes.Institution, id: 'LIST' },
            ]
          : [],
    }),

    /**
     * Start a sync of institution data from providers.
     *
     * Triggers an async sync of accounts, transactions, and balances from connected
     * providers (Plaid, etc.). Returns a `batch_id`; use
     * `syncInstitutionsSyncBatchIdStatus` with that ID to poll until
     * `is_completed`.
     *
     * @remarks
     * **Base endpoint:** `POST /v2/advisory/institutions/sync/`
     * @example
     *
     * ```tsx
     * const [syncInstitutions] = advisoryApi.useSyncInstitutionsMutation();
     * const [getSyncStatus] =
     *   advisoryApi.useSyncInstitutionsSyncBatchIdStatusMutation();
     *
     * const result = await syncInstitutions({
     *   client_id: 123,
     *   connection_ids: [1, 2],
     *   start_date: '2024-01-01',
     * }).unwrap();
     *
     * if (result) {
     *   const status = await getSyncStatus({
     *     batch_id: result.batch_id,
     *   }).unwrap();
     * }
     * ```
     *
     * @param params - See
     *   {@link BrokerageTypes.CreateAdvisoryInstitutionsSyncParams} and
     *   {@link BrokerageTypes.CreateAdvisoryInstitutionsSyncRequestBody}
     * @returns {@link BrokerageTypes.SyncResponse} With `batch_id` and
     *   `institutions`.
     */
    syncInstitutions: build.mutation<
      BrokerageTypes.CreateAdvisoryInstitutionsSyncResponseBody &
        BrokerageTypes.ClientIdParam,
      BrokerageTypes.CreateAdvisoryInstitutionsSyncParams &
        BrokerageTypes.CreateAdvisoryInstitutionsSyncRequestBody &
        BrokerageTypes.ClientIdParam
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createAdvisoryInstitutionsSyncRaw.initiate({
            ...params,
            connection_ids: params.connection_ids
              .map(id => Number(id))
              .filter(id => !isNaN(id)),
          }),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return {
          data: {
            ...response.data,
            client_id: params.client_id,
          },
        };
      },
      invalidatesTags: [],
    }),

    /**
     * Poll sync status until the batch is completed or errors.
     *
     * Calls the status endpoint every 5 seconds until `is_completed` is true, the
     * API returns an error, or the single-wait cap is exceeded. Use after
     * `syncInstitutions` with the returned `batch_id`.
     *
     * @remarks
     * **Base endpoint:** `GET /v2/advisory/institutions/sync/{batch_id}/status/`
     * @example
     *
     * ```tsx
     * const [getSyncStatus, { isLoading, data }] =
     *   advisoryApi.useSyncInstitutionsSyncBatchIdStatusMutation();
     *
     * const status = await getSyncStatus({ batch_id: '...' }).unwrap();
     * console.log('Completed:', status.is_completed);
     * console.log('Connections:', status.connections);
     * ```
     *
     * @param params - See
     *   {@link BrokerageTypes.SyncInstitutionsSyncBatchIdStatusParams}
     * @returns {@link BrokerageTypes.SyncStatus} When the batch has completed or an
     *   error.
     */
    syncInstitutionsSyncBatchIdStatus: build.mutation<
      BrokerageTypes.GetAdvisoryInstitutionsSyncBatchIdStatusResponseBody &
        BrokerageTypes.ClientIdParam,
      BrokerageTypes.GetAdvisoryInstitutionsSyncBatchIdStatusParams &
        BrokerageTypes.ClientIdParam
    >({
      queryFn: async (params, api) => {
        const result = await pollWithBackoff<
          BrokerageTypes.GetAdvisoryInstitutionsSyncBatchIdStatusResponseBody &
            BrokerageTypes.ClientIdParam,
          FetchBaseQueryError
        >({
          poll: async () => {
            const response = await api.dispatch(
              rawApi.endpoints.getAdvisoryInstitutionsSyncBatchIdStatusRaw.initiate(
                params,
              ),
            );

            if (response.error) {
              return { error: response.error as FetchBaseQueryError };
            }

            return {
              data: {
                ...response.data,
                client_id: params.client_id,
              },
            };
          },
          shouldContinue: res =>
            !res.error && res.data !== null && !res.data.is_completed,
          getResult: res => (res.error ? { error: res.error } : { data: res.data }),
          pollInterval: 5000,
          maxWait: 900_000,
          backoff: 'constant',
        });

        return result;
      },
      invalidatesTags: (result, error) => {
        const tags =
          result && !error
            ? [
                { type: BrokerageTagTypes.Client, id: 'LIST' },
                { type: BrokerageTagTypes.Institution, id: 'LIST' },
                ...Object.keys(result.connections ?? {}).map(id => ({
                  type: BrokerageTagTypes.Aggregator as const,
                  id: `INSTITUTION_${id}`,
                })),
                {
                  type: BrokerageTagTypes.Aggregator as const,
                  id: 'INSTITUTION_ALL',
                },
              ]
            : [];

        return tags;
      },
    }),

    /**
     * Add Citec User Client
     *
     * This endpoint associates a new client with the Citec user.
     *
     * @remarks
     * Used to link a client profile to the current authenticated Citec user
     * account. The payload is automatically cleaned to match the required API
     * structure.
     *
     * **Base endpoint:** `POST /v2/advisory/clients/`
     * @example
     *
     * ```tsx
     * const [
     *   addCitecUserClient, // Mutation trigger
     *   { isLoading: isAdding }, // Loading state
     * ] = advisoryApi.useAddCitecUserClientMutation();
     *
     * addCitecUserClient({
     *   email: 'john@doe.com',
     *   first_name: 'John',
     *   first_surname: 'Doe',
     * });
     * ```
     *
     * @param params - Client data to associate with current user. See
     *   {@link BrokerageTypes.CreateAdvisoryClientsRequestBody}
     * @param params.email - Client's email address (required)
     * @param params.first_name - (Optional) Client's first or given name
     * @param params.second_name - (Optional) Client's second or middle name
     * @param params.first_surname - (Optional) Client's first (paternal) surname
     * @param params.second_surname - (Optional) Client's second (maternal) surname
     * @returns Response with created client association data. See
     *   {@link BrokerageTypes.CreateAdvisoryClientsResponseBody}
     */
    addCitecUserClient: build.mutation<
      BrokerageTypes.CreateAdvisoryClientsResponseBody,
      BrokerageTypes.CreateAdvisoryClientsRequestBody
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createAdvisoryClientsRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          const { data: client } = await queryFulfilled;

          // Clear advisory repository cache for the client in case of reconnection
          if (!isUndefined(client?.id)) {
            dispatch(
              advisoryRepositorySlice.actions.resetByClientId({
                clientId: client.id,
              }),
            );
          }
        } catch (error) {
          console.error('Error in addCitecUserClient:', error);
        }
      },
      invalidatesTags: (result, error) =>
        result && !error ? [{ type: BrokerageTagTypes.Client, id: 'LIST' }] : [],
    }),

    /**
     * Delete Citec User Client
     *
     * Removes a client association from the current Citec user.
     *
     * @remarks
     * Deletes the link between a specific client and the authenticated user
     * account. Uses client ID to identify which association to remove.
     *
     * **Base endpoint:** `DELETE /v2/advisory/clients/{client_id}/`
     * @example
     *
     * ```tsx
     * const [
     *   deleteCitecUserClient, // Mutation trigger
     *   { isSuccess: isDeleted }, // Success state
     * ] = advisoryApi.useDeleteCitecUserClientMutation();
     *
     * deleteCitecUserClient({ client_id: 123 });
     * ```
     *
     * @param params - See {@link BrokerageTypes.DeleteAdvisoryClientsClientIdParams}
     * @param params.client_id - ID of the client to disassociate
     * @returns Empty response on successful deletion. See
     *   {@link BrokerageTypes.DeleteAdvisoryClientsClientIdResponseBody}
     */
    deleteCitecUserClient: build.mutation<
      BrokerageTypes.DeleteAdvisoryClientsClientIdResponseBody,
      BrokerageTypes.DeleteAdvisoryClientsClientIdParams
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.deleteAdvisoryClientsClientIdRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          if (!isUndefined(params.client_id)) {
            dispatch(
              advisoryRepositorySlice.actions.resetByClientId({
                clientId: params.client_id,
              }),
            );
          }
        } catch (error) {
          console.error('Error in deleteCitecUserClient:', error);
        }
      },
      invalidatesTags: (result, error) =>
        !isUndefined(result) && !error
          ? [{ type: BrokerageTagTypes.Client, id: 'LIST' }]
          : [],
    }),

    /**
     * Get client profile by client ID.
     *
     * Retrieves the profile for a specific client including personal data,
     * institutions, advisory proposal, and risk profile. Uses the raw API and
     * returns the new ClientProfile structure (brokerage-types).
     *
     * **Base endpoint:** `GET /v2/advisory/clients/{client_id}/profile/`
     */
    getClientProfile: build.query<
      BrokerageTypes.GetAdvisoryClientsClientIdProfileResponseBody,
      BrokerageTypes.GetAdvisoryClientsClientIdProfileParams
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryClientsClientIdProfileRaw.initiate(params, {
            forceRefetch: api.forced,
          }),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const data =
          response.data as BrokerageTypes.GetAdvisoryClientsClientIdProfileResponseBody;
        return { data };
      },
      providesTags: (result, error, params) =>
        result
          ? [
              { type: BrokerageTagTypes.Client, id: 'Profile' },
              { type: BrokerageTagTypes.Client, id: params.client_id },
            ]
          : [{ type: BrokerageTagTypes.Client, id: 'Profile' }],
    }),

    /**
     * Create an advisory proposal.
     *
     * Creates a new advisory proposal for a client with institutions, advisory
     * type, and commission. Uses the raw API and the new CreateAdvisoryProposal
     * types (brokerage-types).
     *
     * **Base endpoint:** `POST /v2/advisory/proposals/`
     */
    createAdvisoryProposal: build.mutation<
      BrokerageTypes.CreateAdvisoryProposalsResponseBody,
      BrokerageTypes.CreateAdvisoryProposalsRequestBody
    >({
      queryFn: async (body, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createAdvisoryProposalsRaw.initiate(body),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      invalidatesTags: (_, error) =>
        error
          ? []
          : [
              { type: BrokerageTagTypes.Client, id: 'Profile' },
              { type: BrokerageTagTypes.Client, id: 'LIST' },
              { type: BrokerageTagTypes.Proposals, id: 'List' },
            ],
    }),

    /**
     * Post notification (e.g. advisory_proposal).
     *
     * **Base endpoint:** `POST /v2/notification/`
     */
    notification: build.mutation<
      BrokerageTypes.CreateNotificationResponseBody,
      BrokerageTypes.CreateNotificationRequestBody
    >({
      queryFn: async (body, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createNotificationRaw.initiate(body),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      extraOptions: {
        useTrackPromise: true,
      },
      invalidatesTags: [],
    }),

    /**
     * Get advisors with their shared institutions for retail user.
     *
     * Retrieves a list of advisors who have shared institutions with the current
     * retail user. This is used to display which advisors have access to the user's
     * financial institution data.
     *
     * @remarks
     * This endpoint is used in the retail user context to see which advisors have
     * been granted access to view their financial institution data. It returns
     * advisor information along with the institutions that are shared with each
     * advisor.
     *
     * **Base endpoint:** `GET /v2/advisory/users/`
     * @example
     *
     * ```tsx
     * const { data: advisors, isLoading } =
     *   advisoryApi.useGetAdvisoryUsersQuery();
     *
     * // Display advisors with shared institutions
     * advisors?.forEach(advisor => {
     *   console.log(`Advisor: ${advisor.name || advisor.email}`);
     *   console.log(
     *     `Shared institutions: ${advisor.institutions?.length || 0}`,
     *   );
     *   advisor.institutions?.forEach(inst => {
     *     console.log(`  - ${inst.name}`);
     *   });
     * });
     * ```
     *
     * @returns An array of {@link BrokerageTypes.GetAdvisoryUsersResponseBody}
     *   containing advisor information including name, email, and shared
     *   institutions.
     */
    getAdvisoryUsers: build.query<
      BrokerageTypes.GetAdvisoryUsersResponseBody,
      void
    >({
      queryFn: async (_, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryUsersRaw.initiate(void 0, {
            forceRefetch: api.forced,
          }),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data ?? [] };
      },
      providesTags: result =>
        result
          ? [
              { type: BrokerageTagTypes.Client, id: 'ADVISORS_LIST' },
              ...result.map(advisor => ({
                type: BrokerageTagTypes.Client as const,
                id: advisor.id.toString() || `ADVISOR_${advisor.email}`,
              })),
            ]
          : [{ type: BrokerageTagTypes.Client, id: 'ADVISORS_LIST' }],
    }),

    /**
     * Share accounts with users.
     *
     * Shares specific accounts with another user (typically an advisor) by
     * providing their email address and the IBANs of the accounts to share.
     *
     * @remarks
     * This endpoint establishes a sharing relationship for specific accounts. The
     * recipient user will be able to view data from the shared accounts once they
     * confirm the sharing request.
     *
     * **Base endpoint:** `POST /v2/advisory/users/accounts/`
     * @example
     *
     * ```tsx
     * const [shareAccounts, { isLoading, isSuccess }] =
     *   advisoryApi.useShareAdvisoryUsersAccountsMutation();
     *
     * await shareAccounts({
     *   user_email: 'advisor@example.com',
     *   ibans: ['ES1234567890123456789012'],
     * });
     *
     * if (isSuccess) {
     *   console.log('Accounts shared successfully');
     * }
     * ```
     *
     * @param params - An object containing:
     *
     *   - `user_email`: Email of the user to share accounts with
     *   - `ibans`: (Optional) Array of IBANs to share
     *
     * @returns Response with sharing confirmation data.
     */
    shareAdvisoryUsersAccounts: build.mutation<
      BrokerageTypes.CreateAdvisoryUsersAccountsResponseBody,
      BrokerageTypes.CreateAdvisoryUsersAccountsRequestBody
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.createAdvisoryUsersAccountsRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      invalidatesTags: (result, error) =>
        result && !error
          ? [
              { type: BrokerageTagTypes.Client, id: 'ADVISORS_LIST' },
              { type: BrokerageTagTypes.Client, id: 'LIST' },
            ]
          : [],
    }),

    /**
     * Confirm or reject shared accounts.
     *
     * Confirms or rejects a sharing request for specific accounts. This endpoint is
     * used by the recipient user to accept or decline account sharing requests.
     *
     * @remarks
     * This endpoint allows the recipient user to confirm or reject account sharing
     * requests. When confirmed, the user gains access to view data from the shared
     * accounts. When rejected, the sharing request is declined.
     *
     * **Base endpoint:** `PATCH /v2/advisory/users/accounts/`
     * @example
     *
     * ```tsx
     * const [confirmAccounts, { isLoading, isSuccess }] =
     *   advisoryApi.useUpdateAdvisoryUsersAccountsMutation();
     *
     * await confirmAccounts({
     *   ibans: ['ES1234567890123456789012'],
     *   confirm: true,
     * });
     *
     * if (isSuccess) {
     *   console.log('Accounts confirmed successfully');
     * }
     * ```
     *
     * @param params - An object containing:
     *
     *   - `ibans`: (Optional) Array of IBANs to confirm or reject
     *   - `confirm`: Boolean indicating whether to confirm (true) or reject (false)
     *
     * @returns Void response (204) upon successful update.
     */
    updateAdvisoryUsersAccounts: build.mutation<
      BrokerageTypes.UpdateAdvisoryUsersAccountsResponseBody,
      BrokerageTypes.UpdateAdvisoryUsersAccountsRequestBody & {
        client_id: number;
        institution_id: string;
      }
    >({
      queryFn: async (params, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.updateAdvisoryUsersAccountsRaw.initiate(params),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          // Update clients slice immediately to reflect the change
          if (params.ibans && params.ibans.length > 0) {
            dispatch(
              clientsSlice.actions.updateClientAccountStatus({
                ibans: params.ibans,
                confirm: params.confirm,
              }),
            );
          }

          dispatch(
            advisoryRepositorySlice.actions.resetByClientAndInstitutionId({
              clientId: params.client_id,
              institutions: [params.institution_id],
            }),
          );
        } catch (error) {
          console.error('Error in updateAdvisoryUsersAccounts:', error);
        }
      },
      invalidatesTags: (result, error, params) =>
        !isUndefined(result) && !error
          ? [
              { type: BrokerageTagTypes.Client, id: 'ADVISORS_LIST' },
              { type: BrokerageTagTypes.Client, id: 'LIST' },
              {
                type: BrokerageTagTypes.Aggregator,
                id: `INSTITUTION_${params.institution_id}`,
              },
              {
                type: BrokerageTagTypes.Aggregator,
                id: 'INSTITUTION_ALL',
              },
              {
                type: BrokerageTagTypes.Client,
                id: `DEBT_${params.client_id}`,
              },
              SharedTagTypes.CurrencyConversion,
            ]
          : [],
    }),

    /**
     * Unshare shared accounts.
     *
     * Revokes access to specific accounts that were previously shared with an
     * advisor. This removes the advisor's ability to view data from the specified
     * accounts while maintaining access to other shared accounts.
     *
     * @remarks
     * This endpoint allows granular control over account sharing. You can unshare
     * specific accounts by providing their IBANs, while leaving other accounts
     * shared. This is useful when a client wants to revoke access to specific
     * accounts without removing the advisor relationship entirely.
     *
     * **Base endpoint:** `PATCH /v2/advisory/users/accounts/unshare/`
     * @example
     *
     * ```tsx
     * const [unshareAccounts, { isLoading, isSuccess }] =
     *   advisoryApi.useUnshareAdvisoryUsersAccountsMutation();
     *
     * await unshareAccounts({
     *   client_id: 123,
     *   ibans: ['ES1234567890123456789012'],
     *   user_id: 456,
     * });
     *
     * if (isSuccess) {
     *   console.log('Accounts unshared successfully');
     * }
     * ```
     *
     * @param params - An object containing:
     *
     *   - `client_id`: (Optional) Client ID for the account sharing context
     *   - `ibans`: Array of IBANs to unshare
     *   - `user_id`: ID of the user to unshare accounts from
     *   - `institution_id`: (Internal) Institution ID used for state management, not
     *       sent to API
     *
     * @returns Void response (204) upon successful update.
     */
    /**
     * Get debt data for advisory accounts.
     *
     * Returns credit lines and loans for the institution's accounts together with a
     * daily historical series of outstanding debt classified by maturity
     * (short-term ≤ 12 months, long-term > 12 months). Monetary values are returned
     * in the requested currency (converted server-side).
     *
     * @remarks
     * **Cache:** Per IBAN for `currency`, `client_id`, `start_date`, and
     * `end_date`. Only missing IBANs for the institution scope are requested from
     * raw API.
     *
     * - `dates` is a single shared time axis (no per-account duplication).
     * - `accounts[].historical_debt.{short_term,long_term}` arrays align to `dates`.
     * - `credit_lines[].amortizations` and `loans[].amortizations` contain the
     *   payment schedule; the frontend filters pending items by date windows.
     *
     * **Base endpoint:** `POST /v2/advisory/accounts/debts/`
     * @example
     *
     * ```tsx
     * const { data: debt, isLoading } =
     *   advisoryApi.useGetAdvisoryAccountsDebtQuery({
     *     institution_id: 'all',
     *     start_date: '2026-01-01',
     *     end_date: '2026-03-19',
     *     currency: 'EUR',
     *     client_id: 123,
     *   });
     * ```
     *
     * @param params.institution_id - Institution scope (`all` or institution id)
     * @param params.start_date - Start of the date range (inclusive)
     * @param params.end_date - End of the date range (inclusive)
     * @param params.currency - Target currency for monetary values
     * @param params.client_id - Optional client ID (advisor context)
     * @returns {@link BrokerageTypes.AdvisoryAccountsDebt}
     */
    getAdvisoryAccountsDebt: build.query<
      BrokerageTypes.AdvisoryAccountsDebt,
      Omit<BrokerageTypes.GetAdvisoryAccountsDebtRequestBody, 'ibans'> & {
        institution_id: string;
        currency: AccountCurrency;
      }
    >({
      queryFn: async ({ institution_id, currency, ...params }, api) => {
        const ibans = selectClientProductsAccountsByInstitutionId(
          api.getState() as any,
          institution_id,
        );

        const { cached, missingIbans } = getCachedAccountsDebtDiff(
          api.getState() as any,
          currency,
          params.client_id ?? 0,
          ibans,
          params.start_date,
          params.end_date ?? '',
        );

        const payload =
          api.forced && missingIbans.length > 0 ? missingIbans : undefined;

        if (missingIbans.length === 0) {
          return {
            data: {
              currency,
              dates: [],
              accounts: cached,
            },
          };
        }

        const response = await api.dispatch(
          rawApi.endpoints.getAdvisoryAccountsDebtRaw.initiate(
            {
              ...params,
              currency,
              ibans: payload,
            },
            {
              forceRefetch: api.forced,
            },
          ),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const accountsDebt = response.data ?? {
          currency,
          dates: [],
          accounts: [],
        };

        api.dispatch(
          advisoryRepositorySlice.actions.upsertAdvisoryAccountsDebt(
            transformAccountsDebtForCache(
              accountsDebt,
              currency,
              params.client_id ?? 0,
              params.start_date,
              params.end_date ?? '',
            ),
          ),
        );

        const data = {
          currency,
          dates: accountsDebt.dates,
          accounts: cached.concat(accountsDebt.accounts),
        };

        return { data };
      },
      providesTags: (result, _error, params) =>
        result
          ? [
              {
                type: BrokerageTagTypes.Client as const,
                id: `DEBT_${params.client_id ?? 'ALL'}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `DEBT_RANGE_${params.start_date}_${params.end_date ?? 'TODAY'}`,
              },
              {
                type: BrokerageTagTypes.Aggregator as const,
                id: `INSTITUTION_${params.institution_id}`,
              },
              SharedTagTypes.CurrencyConversion,
            ]
          : [],
    }),

    /**
     * Unshare shared accounts.
     *
     * Revokes access to specific accounts that were previously shared with an
     * advisor. This removes the advisor's ability to view data from the specified
     * accounts while maintaining access to other shared accounts.
     *
     * @remarks
     * This endpoint allows granular control over account sharing. You can unshare
     * specific accounts by providing their IBANs, while leaving other accounts
     * shared. This is useful when a client wants to revoke access to specific
     * accounts without removing the advisor relationship entirely.
     *
     * **Base endpoint:** `PATCH /v2/advisory/users/accounts/unshare/`
     * @example
     *
     * ```tsx
     * const [unshareAccounts, { isLoading, isSuccess }] =
     *   advisoryApi.useUnshareAdvisoryUsersAccountsMutation();
     *
     * await unshareAccounts({
     *   client_id: 123,
     *   ibans: ['ES1234567890123456789012'],
     *   user_id: 456,
     * });
     *
     * if (isSuccess) {
     *   console.log('Accounts unshared successfully');
     * }
     * ```
     *
     * @param params - An object containing:
     *
     *   - `client_id`: (Optional) Client ID for the account sharing context
     *   - `ibans`: Array of IBANs to unshare
     *   - `user_id`: ID of the user to unshare accounts from
     *   - `institution_id`: (Internal) Institution ID used for state management, not
     *       sent to API
     *
     * @returns Void response (204) upon successful update.
     */
    unshareAdvisoryUsersAccounts: build.mutation<
      BrokerageTypes.UnshareAdvisoryUsersAccountsResponseBody,
      (BrokerageTypes.UnshareAdvisoryUsersAccountsParams &
        BrokerageTypes.UnshareAdvisoryUsersAccountsRequestBody) & {
        user_id: number;
        institution_id: string;
      }
    >({
      queryFn: async ({ client_id, ibans, user_id }, api) => {
        const response = await api.dispatch(
          rawApi.endpoints.unshareAdvisoryUsersAccountsRaw.initiate({
            client_id,
            ibans,
            user_id,
          }),
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        return { data: response.data };
      },
      onQueryStarted: async (
        { client_id, user_id, institution_id, ibans },
        { dispatch, queryFulfilled },
      ) => {
        try {
          await queryFulfilled;

          // Update advisors slice
          dispatch(
            clientsSlice.actions.removeAdvisorAccount({
              advisorId: user_id,
              institutionId: institution_id,
              ibans,
            }),
          );

          // Update clients slice and clear advisory repository cache if client_id is provided
          if (!isUndefined(client_id)) {
            dispatch(
              clientsSlice.actions.removeClientAccount({
                client_id,
                institutionId: institution_id,
                ibans,
              }),
            );

            // Clear advisory repository cache for the client and institution
            dispatch(
              advisoryRepositorySlice.actions.resetByClientAndInstitutionId({
                clientId: client_id,
                institutions: [institution_id],
              }),
            );
          }
        } catch (error) {
          console.error('Error in unshareAdvisoryUsersAccounts:', error);
        }
      },
      invalidatesTags: (result, error, params) =>
        !isUndefined(result) && !error
          ? [
              { type: BrokerageTagTypes.Client, id: 'LIST' },
              {
                type: BrokerageTagTypes.Aggregator,
                id: `INSTITUTION_${params.institution_id}`,
              },
              {
                type: BrokerageTagTypes.Aggregator,
                id: 'INSTITUTION_ALL',
              },
            ]
          : [],
    }),
  }),
});
